import React, { useEffect, useRef, useState } from "react";
import { Ticket } from "../../../../models/Ticket";
import EAStepper from "../../../shared/Stepper/EAStepper";
import { useTranslation } from "react-i18next";
import useCalendarSale from "../../../CalendarSale/hooks/useCalendarSale";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import useLocaleByLanguage from "../../../../../utils/hooks/useLocaleByLanguage";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import EAButton from "../../../shared/Buttons/EAButton";
import { FaArrowRight } from "react-icons/fa";
import EAChoiceChip from "../../../shared/ChoiceChip/EAChoiceChip";
import { Exercise } from "../../../../models/Exercise";
import HtmlTooltip from "@mui/material/Tooltip";
import EAAlert from "../../../shared/EAAlert";
import "../../../CalendarSale/style.scss";
import "../../../../../style/calendar.scss";
import "./../FutureTickets.scss"
import ChangeTermButton from "../../../shared/Buttons/ChangeTermButton";

interface IProps {
  ticket: Ticket;
  onChangeDateFinish: () => void;
}

const SingleFutureTicketDateSelector: React.FC<IProps> = ({ ticket, onChangeDateFinish }) => {
  const { t } = useTranslation();
  const locale = useLocaleByLanguage();
  const [activeStep, setActiveStep] = useState(0);
  const selectedOrderRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState({ passdb: 0, passIds: 0, exerciseIds: 0 });

  const { listaDat, getDaysAvailableByPassId, getExercisesByPassIds, selectedDate, exercise, getAssignByTicket } =
    useCalendarSale();

  const scrollToSelectedOrder = () => {
    if (selectedOrderRef.current) {
      selectedOrderRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    getDaysAvailableByPassId(ticket.objectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeDate = () => {
    getAssignByTicket(data.passdb, data.passIds, data.exerciseIds, onChangeDateFinish);
  };

  return (
    <EAStepper
      hideSteps
      steps={[
        {
          label: "",
          content: (
            <div className="calendar">
              <DayPicker
                mode="single"
                locale={locale}
                selected={selectedDate}
                fromDate={new Date(Math.max(new Date().setHours(0, 0, 0, 0), new Date(ticket.validfrom).getTime()))}
                toDate={new Date(ticket.validto)}
                onSelect={(date) => {
                  getExercisesByPassIds(date ?? new Date(), `${ticket.objectDb},${ticket.objectId}`);
                  scrollToSelectedOrder();
                }}
                showOutsideDays
                modifiersClassNames={{
                  selected: "my-selected",
                  today: "my-today",
                }}
                disabled={listaDat}
              />
              <div className="calendar-legend flex-start-center">
                <div className="calendar-legend-dot"></div>
                <b>{t("dostepne_terminy")}</b>
              </div>
              {exercise.length === 0 && selectedDate && (
                <EAAlert type="warning" message={t("W tym dniu brak dostępnych zajęć.")} />
              )}
              <EAButton
                title={t("wybierz_godzine")}
                fullWidth
                endIcon={<FaArrowRight />}
                onClick={() => setActiveStep(1)}
                disabled={!exercise[0]}
              />
            </div>
          ),
        },
        {
          label: "",
          content: (
            <div className="single-future-ticket-hours-container" ref={selectedOrderRef}>
              {exercise.length === 0 ? (
                <div className="no-available-exercises">{t("Brak dostępnych godzin")}</div>
              ) : (
                Object.entries(
                  exercise.reduce((groups: { [key: string]: Exercise[] }, item) => {
                    if (!groups[item.name]) {
                      groups[item.name] = [];
                    }
                    groups[item.name].push(item);
                    return groups;
                  }, {})
                ).map(([name, items]) => (
                  <div key={name}>
                    <h5>{name}</h5>
                    <div className="single-future-ticket-hours-wrapper">
                      {items
                        .sort((a, b) => a.startTime - b.startTime)
                        .map((item) => (
                          <>
                            {item.capacity - (item.reserved + item.prereservation) === 0 ? (
                              <HtmlTooltip
                                key={item.objectId}
                                placement="right"
                                title={<EAAlert type="error" message={t("wszystkie_bilety_zostaly_sprzedane")} />}
                              >
                                <div>
                                  <EAChoiceChip
                                    label={`${format(
                                      utcToZonedTime(item.startTime, "Europe/Warsaw"),
                                      "HH:mm"
                                    )} - ${format(utcToZonedTime(item.closeTime, "Europe/Warsaw"), "HH:mm")}`}
                                    isSelected={item.objectId === data.exerciseIds}
                                    disabled
                                    onClick={() => {
                                      setData({
                                        passdb: ticket.objectDb,
                                        passIds: ticket.objectId,
                                        exerciseIds: item.objectId,
                                      });
                                    }}
                                    size="small"
                                    showSelectedIcon={false}
                                  />
                                </div>
                              </HtmlTooltip>
                            ) : (
                              <EAChoiceChip
                                key={item.objectId}
                                label={`${format(utcToZonedTime(item.startTime, "Europe/Warsaw"), "HH:mm")} - ${format(
                                  utcToZonedTime(item.closeTime, "Europe/Warsaw"),
                                  "HH:mm"
                                )}`}
                                isSelected={item.objectId === data.exerciseIds}
                                onClick={() => {
                                  setData({
                                    passdb: ticket.objectDb,
                                    passIds: ticket.objectId,
                                    exerciseIds: item.objectId,
                                  });
                                }}
                                size="small"
                                showSelectedIcon={false}
                              />
                            )}
                          </>
                        ))}
                    </div>
                  </div>
                ))
              )}

              <EAButton title={"Wybierz inny dzień"} fullWidth variant="outlined" onClick={() => setActiveStep(0)} />
              <ChangeTermButton
                onClick={() => onChangeDate()}
                fullWidth
                endIcon={<FaArrowRight />}
                disabled={!data.exerciseIds}
              />
            </div>
          ),
        },
      ]}
      activeStep={activeStep}
      onStepChange={(stepNumber) => setActiveStep(stepNumber)}
    />
  );
};

export default SingleFutureTicketDateSelector;
